import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type CourierDispatchJob, type CourierReleaseAttribution, type CourierReleaseReason } from '@vello/api-client';
import { Badge, Button, Cluster, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, FieldCell, InlineStack, KeyValue, KeyValueList, ListTable, Notice, PageHeader, Panel, SectionTitle, Select, Text, type Column, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  COURIER_JOB_KINDS, COURIER_JOB_STAGES, courierJobStuck, courierStepMinutes, filterCourierJobs,
  formatMarketInstant, marketTimeZone, mayReleaseCourierJobs, releaseDoneMessageId,
  releaseRefusalRefreshesList, shortId, sortCourierJobs, stuckCourierJobCount,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCourierStep } from '../i18n/courier-step';
import { DrawerStack } from '../components/drawer-stack';
import { PersonName } from '../components/person-name';
import { CourierReleaseDialog } from '../components/courier-release-dialog';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { CourierIncidentsPanel } from '../components/courier-incidents-panel';

/**
 * Operations › Couriers on jobs (W4-COURIER-RELEASE, D378, ADR-0051) — where the Admin support persona (the atendente at
 * its city, or OPS/admins) finds a courier stuck on a courier job and releases it:
 *
 *   - the market's courier jobs (`GET /v1/courier-dispatch/jobs?marketId=`), STUCK first — a locked handover PIN, or a
 *     courier whose last step is older than `COURIER_STUCK_AFTER_SECONDS` — then newest first as the server returns;
 *     re-read every 15 s while the tab is visible;
 *   - the reason-coded release through the design-system dialog (`CourierReleaseDialog`, ≥48 px targets). The
 *     idempotency key is minted ONCE per opening, so pressing confirm again after a failure replays instead of
 *     releasing twice. A refusal keeps the dialog open with the reason in words; a stale-row refusal re-reads the list.
 *
 * The market is the scoped city; without one, a picker of the operating markets (narrowed to the scoped country). The
 * server authorizes `courier_job:view` / `courier_job:release` at the job's market and decides the outcome: before the
 * pickup the job is offered to ANOTHER courier; after it the job ends not delivered. One primary action: the dialog's.
 */
function newKey(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** One opening of the release dialog: the row snapshot, its market, and the idempotency key minted for it. */
interface ReleaseTarget {
  readonly job: CourierDispatchJob;
  readonly marketId: string;
  readonly idempotencyKey: string;
}

export function CourierJobsScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { toast } = useConsole();
  const { t, tp, lang } = useT();
  const { fail, v, refusal: refusalOf } = useVocab();
  const courierStep = useCourierStep();
  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);
  const mayRelease = mayReleaseCourierJobs(grants);

  const [jobs, setJobs] = useState<readonly CourierDispatchJob[] | undefined>(undefined);
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [target, setTarget] = useState<ReleaseTarget | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // The filters (empty = all) and the job whose details are open (by id: it follows the live row).
  const [kindFilter, setKindFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [openJobId, setOpenJobId] = useState<string | undefined>(undefined);
  const [releaseError, setReleaseError] = useState<string | undefined>(undefined);
  // Only the latest read may land (a market switch or a poll can overtake an older answer).
  const readSeq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setJobs(undefined);
      setListError(undefined);
      return;
    }
    const res = await runtime.courierDispatch.jobs(market);
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setJobs(res.value.jobs);
      setListError(undefined);
    } else {
      setJobs((prev) => prev ?? []);
      setListError(refusalOf('refusal.admin.courier_job_list', res.error) ?? t('cjobs.list.generic', { code: fail(res.error) }));
    }
  }, [runtime, market, t, fail, refusalOf]);

  // A new market starts from "loading", never from the previous market's rows.
  useEffect(() => {
    setJobs(undefined);
    void load();
  }, [load]);

  // Live board: re-read while the tab is visible, and at once when it becomes visible again.
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live, enabled: market !== undefined });

  const openRelease = (job: CourierDispatchJob): void => {
    if (market === undefined) return;
    setTarget({ job, marketId: market, idempotencyKey: newKey() });
    setReleaseError(undefined);
    setDialogOpen(true);
  };

  const closeRelease = useCallback((): void => {
    if (!busy) setDialogOpen(false);
  }, [busy]);

  const confirmRelease = async (input: { readonly reasonCode: CourierReleaseReason; readonly note?: string; readonly attribution?: CourierReleaseAttribution }): Promise<void> => {
    // The courier on the row the operator OPENED (the snapshot, never the live row): if the job went to another courier
    // meanwhile, the server refuses (`COURIER_JOB_CHANGED`) instead of releasing someone the operator never saw (review R1).
    const courierId = target?.job.courierId ?? null;
    if (target === undefined || busy || courierId === null) return;
    setBusy(true);
    setReleaseError(undefined);
    const res = await runtime.courierDispatch.release(target.job.jobId, {
      marketId: target.marketId,
      courierId,
      reasonCode: input.reasonCode,
      ...(input.attribution !== undefined ? { attribution: input.attribution } : {}),
      ...(input.note !== undefined ? { note: input.note } : {}),
      idempotencyKey: target.idempotencyKey,
    });
    setBusy(false);
    if (res.ok) {
      setDialogOpen(false);
      toast({ message: t(releaseDoneMessageId(res.value.outcome)), tone: 'success' });
      await load();
      return;
    }
    setReleaseError(refusalOf('refusal.admin.courier_job_release', res.error) ?? t('cjobs.refusal.generic', { code: fail(res.error) }));
    if (releaseRefusalRefreshesList(res.error)) await load();
  };

  const rows = useMemo(
    () => filterCourierJobs(sortCourierJobs(jobs ?? []), { kind: kindFilter, stage: stageFilter }),
    [jobs, kindFilter, stageFilter],
  );
  const filtered = kindFilter !== '' || stageFilter !== '';
  const openJob = openJobId === undefined ? undefined : jobs?.find((j) => j.jobId === openJobId);
  // While the dialog is open it follows the live row (a poll or a stale-row refusal may have changed its phase or ended
  // it); once closing it keeps the snapshot, so the exit animation does not re-render another state.
  const dialogJob = target === undefined ? undefined : dialogOpen ? (jobs?.find((j) => j.jobId === target.job.jobId) ?? target.job) : target.job;
  const attention = useMemo(() => stuckCourierJobCount(jobs ?? []), [jobs]);
  const orderOf = (job: CourierDispatchJob): string => job.orderCode ?? shortId(job.sourceOrderId);
  const when = (iso: string | null, job: CourierDispatchJob): string => (iso === null ? '—' : formatMarketInstant(iso, job.timezone ?? timeZone, lang, 'datetime-seconds'));

  const columns: readonly Column<CourierDispatchJob>[] = [
    { key: 'order', header: t('cjobs.col.order'), width: 'minmax(96px, 0.8fr)', render: (job) => <span className="tnum">{orderOf(job)}</span> },
    { key: 'kind', header: t('cjobs.col.kind'), width: 'minmax(110px, 0.7fr)', render: (job) => <Badge tone={job.kind === 'food' ? 'warning' : 'neutral'}>{v('courier.job_kind', job.kind)}</Badge> },
    { key: 'courier', header: t('cjobs.col.courier'), width: 'minmax(96px, 0.8fr)', render: (job) => <PersonName id={job.courierId} /> },
    {
      key: 'step',
      header: t('cjobs.col.step'),
      width: 'minmax(140px, 1.2fr)',
      render: (job) => courierStep(job),
    },
    {
      key: 'stuck',
      header: t('cjobs.col.stuck'),
      width: 'minmax(140px, 1fr)',
      render: (job) => {
        const stuck = courierJobStuck(job);
        if (stuck?.kind === 'pin_locked') return <Badge tone="danger">{t('cjobs.stuck.pinLocked')}</Badge>;
        if (stuck?.kind === 'pickup_locked') return <Badge tone="danger">{t('cjobs.stuck.pickupLocked')}</Badge>;
        if (stuck?.kind === 'idle') return <Badge tone="warning"><span>{t('cjobs.stuck.idle', { minutes: stuck.minutes })}</span></Badge>;
        const minutes = courierStepMinutes(job);
        return minutes !== undefined ? <Text role="caption" tone="secondary">{t('cjobs.stepAge', { minutes })}</Text> : '—';
      },
    },
    {
      key: 'lastRelease',
      header: t('cjobs.col.lastRelease'),
      width: 'minmax(160px, 1.4fr)',
      render: (job) => {
        const last = job.releases[0];
        if (last === undefined) return '—';
        return (
          <InlineStack gap={1}>
            <span>{t('cjobs.lastRelease', { reason: v('courier.release_reason', last.reasonCode), outcome: v('courier.release_outcome', last.outcome) })}</span>
            <Text role="caption" tone="secondary" mono>{formatMarketInstant(last.releasedAt, job.timezone ?? timeZone, lang, 'time')}</Text>
          </InlineStack>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title={t('cjobs.title')} subtitle={t('cjobs.subtitle')} />

      {market !== undefined && (
        <Panel>
          <Cluster gap={4} wrap>
            <FieldCell size="sm">
              <Field label={t('cjobs.filter.kind')} htmlFor="cjobs-kind">
                <Select id="cjobs-kind" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
                  <option value="">{t('cjobs.filter.allKinds')}</option>
                  {COURIER_JOB_KINDS.map((k) => (
                    <option key={k} value={k}>{v('courier.job_kind', k)}</option>
                  ))}
                </Select>
              </Field>
            </FieldCell>
            <FieldCell size="sm">
              <Field label={t('cjobs.filter.stage')} htmlFor="cjobs-stage">
                <Select id="cjobs-stage" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
                  <option value="">{t('cjobs.filter.allStages')}</option>
                  {COURIER_JOB_STAGES.map((st) => (
                    <option key={st.key} value={st.key}>{courierStep(st)}</option>
                  ))}
                </Select>
              </Field>
            </FieldCell>
          </Cluster>
        </Panel>
      )}

      {attention > 0 && (
        <div>
          <Notice tone="warning">
            <span className="tnum">{tp('cjobs.attention', attention)}</span>
          </Notice>
        </div>
      )}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <ListTable
          columns={columns}
          rows={rows}
          rowKey={(job) => job.jobId}
          onRowClick={(job) => setOpenJobId(job.jobId)}
          {...(openJobId !== undefined ? { selectedKey: openJobId } : {})}
          actions={(job) =>
            mayRelease && job.release.allowed ? (
              <Button variant="secondary" aria-label={t('cjobs.release.actionFor', { order: orderOf(job) })} disabled={busy} onClick={() => openRelease(job)}>
                {t('cjobs.release.action')}
              </Button>
            ) : null
          }
          loading={jobs === undefined}
          loadingLabel={t('ui.loading')}
          error={listError}
          onRetry={() => void load()}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={t(filtered ? 'cjobs.empty.filtered' : 'cjobs.empty.title')} detail={t('cjobs.empty.detail')} />}
          minWidth={960}
        />
      )}

      {/* D473 (D-34): what couriers reported from the street, and how an operator closed each report. */}
      {market !== undefined && <CourierIncidentsPanel market={market} timeZone={timeZone} />}

      {/* Neither Send nor Food has a detail route (their details are drawers inside their own lists), so the row opens one
          here with what the courier-jobs read already holds. */}
      <Drawer open={openJob !== undefined} onClose={() => setOpenJobId(undefined)} labelledBy="cjobs-detail-title">
        {openJob !== undefined && (
          <>
            <DrawerHeader
              titleId="cjobs-detail-title"
              title={t('cjobs.detail.title', { order: orderOf(openJob) })}
              subtitle={<Badge tone={openJob.kind === 'food' ? 'warning' : 'neutral'}>{v('courier.job_kind', openJob.kind)}</Badge>}
            />
            <DrawerBody>
              <DrawerStack>
                {courierJobStuck(openJob)?.kind === 'pin_locked' && <Notice tone="danger">{t('cjobs.stuck.pinLocked')}</Notice>}
                {courierJobStuck(openJob)?.kind === 'pickup_locked' && <Notice tone="danger">{t('cjobs.stuck.pickupLocked')}</Notice>}
                {courierJobStuck(openJob)?.kind === 'idle' && <Notice tone="warning">{t('cjobs.stuck.idle', { minutes: courierStepMinutes(openJob) ?? 0 })}</Notice>}
                <KeyValueList>
                  <KeyValue label={t('cjobs.detail.courier')}><PersonName id={openJob.courierId} /></KeyValue>
                  <KeyValue label={t('cjobs.detail.step')}>{courierStep(openJob)}</KeyValue>
                  <KeyValue label={t('cjobs.detail.created')} mono>{when(openJob.createdAt, openJob)}</KeyValue>
                  <KeyValue label={t('cjobs.detail.assigned')} mono>{when(openJob.assignedAt, openJob)}</KeyValue>
                  <KeyValue label={t('cjobs.detail.lastStep')} mono>{when(openJob.updatedAt, openJob)}</KeyValue>
                  <KeyValue label={t('cjobs.detail.completed')} mono>{when(openJob.completedAt, openJob)}</KeyValue>
                </KeyValueList>
                <section>
                  <SectionTitle level={3} size="sub">{t('cjobs.detail.releases')}</SectionTitle>
                  {openJob.releases.length === 0 ? (
                    <Text as="p" role="caption" tone="secondary">{t('cjobs.detail.noReleases')}</Text>
                  ) : (
                    <KeyValueList dense>
                      {openJob.releases.map((r) => (
                        <KeyValue key={r.releaseId} label={formatMarketInstant(r.releasedAt, openJob.timezone ?? timeZone, lang, 'datetime-seconds')} mono>
                          {t('cjobs.lastRelease', { reason: v('courier.release_reason', r.reasonCode), outcome: v('courier.release_outcome', r.outcome) })}
                          {' · '}
                          <PersonName id={r.courierId} />
                        </KeyValue>
                      ))}
                    </KeyValueList>
                  )}
                </section>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              {mayRelease && openJob.release.allowed && (
                <Button variant="secondary" disabled={busy} onClick={() => { setOpenJobId(undefined); openRelease(openJob); }}>
                  {t('cjobs.release.action')}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setOpenJobId(undefined)}>{t('ui.close')}</Button>
            </DrawerFooter>
          </>
        )}
      </Drawer>

      <CourierReleaseDialog
        open={dialogOpen}
        job={dialogJob}
        busy={busy}
        error={releaseError}
        onCancel={closeRelease}
        onConfirm={(input) => void confirmRelease(input)}
      />
    </>
  );
}
